import { useMemo, useState } from "react";
import { MIGRATION } from "../../../constants/testIds";
import { CANONICAL_FIELDS, findDuplicateGroups } from "../../../lib/migrationParse";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";

const BULK_FILLABLE = CANONICAL_FIELDS.filter((f) => !f.required);

// SCR-P04-12 - duplicate rows (same phone, seen across one or more files) get a per-group
// merge/keep-primary/send-both decision; a bulk-fill toolbar applies one default value to every
// row currently missing a given optional field, in a single action.
export function DedupeStep({ mappedRows, dedupeDecisions, setDedupeDecisions, fieldOverrides, setFieldOverrides, onBack, onContinue }) {
  const [bulkField, setBulkField] = useState("");
  const [bulkValue, setBulkValue] = useState("");

  const rowsWithOverrides = useMemo(
    () => mappedRows.map((r) => ({ ...r, ...(fieldOverrides[r.row_key] || {}) })),
    [mappedRows, fieldOverrides],
  );
  const groups = useMemo(() => findDuplicateGroups(rowsWithOverrides), [rowsWithOverrides]);
  const missingCountFor = (field) => rowsWithOverrides.filter((r) => !r[field] && r[field] !== 0).length;

  const applyBulkFill = () => {
    if (!bulkField || !bulkValue) return;
    setFieldOverrides((prev) => {
      const next = { ...prev };
      rowsWithOverrides.forEach((r) => {
        if (!r[bulkField] && r[bulkField] !== 0) {
          next[r.row_key] = { ...(next[r.row_key] || {}), [bulkField]: bulkValue };
        }
      });
      return next;
    });
    setBulkValue("");
  };

  return (
    <div className="space-y-4">
      <div className="solid-surface rounded-[14px] p-5">
        <p className="text-sm font-semibold mb-1" style={{ color: "var(--scarlet-text)" }}>Bulk-fill defaults</p>
        <p className="text-xs mb-3" style={{ color: "var(--scarlet-text-secondary)" }}>Apply one value to every row currently missing a field - in a single action, not row by row.</p>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={bulkField} onValueChange={setBulkField}>
            <SelectTrigger data-testid={MIGRATION.bulkFillFieldSelect} className="w-56"><SelectValue placeholder="Choose a field" /></SelectTrigger>
            <SelectContent>
              {BULK_FILLABLE.map((f) => <SelectItem key={f.key} value={f.key}>{f.label} ({missingCountFor(f.key)} missing)</SelectItem>)}
            </SelectContent>
          </Select>
          <Input data-testid={MIGRATION.bulkFillValueInput} placeholder="Default value" value={bulkValue} onChange={(e) => setBulkValue(e.target.value)} className="w-48" />
          <Button data-testid={MIGRATION.bulkFillApplyButton} size="sm" variant="outline" disabled={!bulkField || !bulkValue} onClick={applyBulkFill}>Apply to all missing</Button>
        </div>
      </div>

      <div className="space-y-2.5">
        <p className="text-sm font-semibold" style={{ color: "var(--scarlet-text)" }}>
          {groups.length === 0 ? "No duplicates detected" : `${groups.length} duplicate ${groups.length === 1 ? "pair" : "pairs"} found (same phone number)`}
        </p>
        {groups.map((g) => {
          const decision = dedupeDecisions[g.key] || { action: "merge" };
          return (
            <div key={g.key} data-testid={MIGRATION.dedupeGroupCard(g.key)} className="solid-surface rounded-[12px] p-4 space-y-2">
              <p className="text-xs" style={{ color: "var(--scarlet-text-secondary)" }}>Phone {g.key}</p>
              {g.rows.map((r) => (
                <div key={r.row_key} className="text-sm flex items-center gap-3" style={{ color: "var(--scarlet-text)" }}>
                  <span className="font-medium">{r.name || "(no name)"}</span>
                  <span style={{ color: "var(--scarlet-text-secondary)" }}>from {r.source_file}</span>
                </div>
              ))}
              <Select value={decision.action} onValueChange={(v) => setDedupeDecisions((prev) => ({ ...prev, [g.key]: { action: v, primaryRowKey: g.rows[0].row_key } }))}>
                <SelectTrigger data-testid={MIGRATION.dedupeActionSelect(g.key)} className="w-64"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="merge">Merge (recommended)</SelectItem>
                  <SelectItem value="keep_primary">Keep first row only, discard other(s)</SelectItem>
                  <SelectItem value="send_both">Send both as-is (still becomes one member - same phone)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          );
        })}
      </div>

      <div className="flex justify-between">
        <Button variant="outline" onClick={onBack}>Back</Button>
        <Button data-testid={MIGRATION.wizardContinueButton} onClick={onContinue} style={{ background: "var(--scarlet-accent)", color: "#fff" }}>Continue</Button>
      </div>
    </div>
  );
}
